import { useMemo, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import VideoListItem from "@/components/feature/VideoListItem";
import { categories, categoryNameMap, videos } from "@/mocks/videos";
import { usePageMeta } from "@/hooks/usePageMeta";

const filterChips = [
  { key: "all", label: "全部" },
  ...categories.map((c) => ({ key: c.key, label: c.name })),
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const [keyword, setKeyword] = useState(q);
  const [category, setCategory] = useState("all");

  usePageMeta(
    q ? `搜索「${q}」- 亲嘴视频` : "搜索亲吻视频 - 亲嘴视频",
    q
      ? `亲嘴视频搜索「${q}」的结果页面，为您找到相关亲吻视频内容。`
      : "在亲嘴视频中搜索影视片段、短视频与情感专辑等亲吻视频内容。"
  );

  useEffect(() => {
    setKeyword(q);
  }, [q]);

  const results = useMemo(() => {
    const kw = q.trim().toLowerCase();
    return videos.filter((v) => {
      const categoryOk = category === "all" || v.categoryKey === category;
      if (!categoryOk) return false;
      if (!kw) return true;
      return (
        v.title.toLowerCase().includes(kw) ||
        v.description.toLowerCase().includes(kw) ||
        v.source.toLowerCase().includes(kw) ||
        v.tags.some((t) => t.toLowerCase().includes(kw)) ||
        categoryNameMap[v.categoryKey].toLowerCase().includes(kw)
      );
    });
  }, [q, category]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = keyword.trim();
    if (trimmed) setSearchParams({ q: trimmed });
    else setSearchParams({});
  };

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6 flex-wrap">
          <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-800">搜索</span>
        </nav>

        <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-6">
          搜索亲吻视频
        </h1>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col sm:flex-row gap-3 max-w-3xl mb-6"
        >
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-lg" />
            <input
              type="text"
              name="q"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="输入关键词，如：初吻、雨夜、重逢…"
              className="w-full h-12 pl-12 pr-4 rounded-md bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors"
            />
          </div>
          <button
            type="submit"
            className="h-12 px-7 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </form>

        {q && (
          <div className="mb-6">
            <p className="text-sm text-foreground-600">
              关键词
              <span className="mx-1.5 px-2 py-0.5 rounded bg-primary-100 text-primary-700 font-medium">
                {q}
              </span>
              共找到
              <span className="mx-1 text-foreground-950 font-medium">
                {results.length}
              </span>
              部相关视频
            </p>
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap mb-8">
          <span className="text-sm text-foreground-500 font-label mr-1">分类筛选</span>
          {filterChips.map((chip) => (
            <button
              key={chip.key}
              onClick={() => setCategory(chip.key)}
              className={`px-3.5 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                category === chip.key
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-primary-100"
              }`}
            >
              {chip.label}
            </button>
          ))}
        </div>

        {!q ? (
          <div className="py-20 text-center rounded-lg bg-background-100 border border-background-200/70">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
              <i className="ri-search-eye-line text-3xl" />
            </div>
            <p className="text-foreground-700 mb-1">输入关键词开始搜索</p>
            <p className="text-sm text-foreground-500 mb-5">
              试试搜索「初吻」「雨夜」「重逢」等热门关键词
            </p>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              {["初吻", "雨夜", "重逢", "樱花"].map((kw) => (
                <button
                  key={kw}
                  onClick={() => setSearchParams({ q: kw })}
                  className="px-3.5 py-1.5 rounded-full bg-background-50 border border-background-200/70 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  {kw}
                </button>
              ))}
            </div>
          </div>
        ) : results.length > 0 ? (
          <div className="space-y-4 md:space-y-5">
            {results.map((video, index) => (
              <VideoListItem key={video.id} video={video} index={index} />
            ))}
          </div>
        ) : (
          <div className="py-20 text-center rounded-lg bg-background-100 border border-background-200/70">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
              <i className="ri-emotion-sad-line text-3xl" />
            </div>
            <p className="text-foreground-700 mb-1">
              没有找到与「{q}」相关的视频
            </p>
            <p className="text-sm text-foreground-500 mb-5">
              换个关键词，或返回首页浏览分类内容
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-4-line" />
              返回首页
            </Link>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}